// ADR-199: Reports → Integrity Checks — runs both scopes in parallel. One
// scope failing never hides the other's results.
import { useCallback, useEffect, useRef, useState } from 'react';
import { integrityChecksApi } from '../services/api/integrityChecksApi';
import { INTEGRITY_SCOPES } from '../constants/integrityChecks';

const SCOPES = Object.values(INTEGRITY_SCOPES);
const empty = () => Object.fromEntries(SCOPES.map((s) => [s, null]));

export const useIntegrityChecks = () => {
  const [reports, setReports] = useState(empty);
  const [errors, setErrors] = useState(empty);
  const [isLoading, setIsLoading] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const mounted = useRef(true);

  const run = useCallback(async ({ initial = false } = {}) => {
    if (initial) setIsLoading(true);
    setIsRunning(true);
    const settled = await Promise.allSettled(SCOPES.map((scope) => integrityChecksApi.run(scope)));
    if (!mounted.current) return;
    const nextReports = empty();
    const nextErrors = empty();
    settled.forEach((outcome, i) => {
      if (outcome.status === 'fulfilled') nextReports[SCOPES[i]] = outcome.value;
      else nextErrors[SCOPES[i]] = outcome.reason;
    });
    setReports(nextReports);
    setErrors(nextErrors);
    setIsLoading(false);
    setIsRunning(false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    run({ initial: true });
    return () => { mounted.current = false; };
  }, [run]);

  const rerun = useCallback(() => run(), [run]);

  return { reports, errors, isLoading, isRunning, rerun, scopes: SCOPES };
};
